export const SCHEDULED_VISITS_ORDER_BY_QUERY = Object.freeze({
  CLOSER_VISITS: 'closer-visits',
  NEW_CLIENTS: 'new-clients',
  NAME: 'client-name',
});

export const SCHEDULED_VISITS_TEXT = Object.freeze({
  PAGE_TITLE: 'Clientes com visita agendada',
  EMPTY_TITLE: 'Você não tem nenhuma visita agendada',
  ERROR_TITLE: 'Serviço indisponível! Tente novamente mais tarde.',
  ORDER_BY_LABEL: 'Ordenar por:',
  ORDER_BY_CLOSEST_VISITS: 'Visitas mais próximas',
  ORDER_BY_NEW_CLIENTS: 'Clientes mais recentes',
  ORDER_BY_ALPHABETICAL: 'Ordem alfabética',
  EXPAND_LABEL: 'Mostrar detalhes',
  COLLAPSE_LABEL: 'Esconder detalhes',
  SEND_MESSAGE: 'Enviar mensagem',
  VIEW_PROFILE: 'Ver perfil completo',
  SEARCH_ORIGIN: 'visitas-agendadas',
});

export const SCHEDULED_VISITS_TRACKING_EVENTS = Object.freeze({
  PAGE_VIEWED: 'clients_scheduled_visit_page_viewed',
  SEARCH_TYPED: 'clients_scheduled_visit_search_typed',
  SORTING_LIST_CLICKED: 'clients_scheduled_visit_sorting_link_clicked',
  SORT_BY_CLOSEST_VISITS: 'clients_scheduled_visit_sort_by_next_visits_clicked',
  SORT_BY_NEW_CLIENTS: 'clients_scheduled_visit_sort_by_latest_clients_clicked',
  SORT_BY_ALPHABETICAL:
    'clients_scheduled_visit_sort_by_alphabetical_order_clicked',
  EXPAND_CARD: 'clients_scheduled_visit_expand_card_clicked',
  SEND_MESSAGE: 'clients_scheduled_visit_send_message_link_clicked',
  VIEW_PROFILE: 'clients_scheduled_visit_view_profile_link_clicked',
  NEXT_PAGE: 'clients_scheduled_visit_next_page_clicked',
  PREVIOUS_PAGE: 'clients_scheduled_visit_previous_page_clicked',
});

export const SCHEDULED_CLIENT_VISIT_STATUSES = Object.freeze([
  'solicitada',
  'agendada',
  'confirmada',
  'paralisada',
]);

const toText = value => String(value || '').trim();

export const scheduledVisitDateValue = visit =>
  visit?.scheduledAt || visit?.scheduled_at || visit?.nextVisitScheduledTo;

export const scheduledClientId = visit =>
  visit?.contact?.id || visit?.contactId || visit?.userClientId;

export const scheduledClientName = visit =>
  toText(
    visit?.contact?.name ||
      visit?.contactName ||
      visit?.clientFullName ||
      visit?.name
  ) || 'Cliente';

export const scheduledClientCreatedAt = visit =>
  visit?.contact?.createdAt ||
  visit?.contact?.created_at ||
  visit?.clientSince ||
  visit?.createdAt ||
  visit?.created_at ||
  '';

export const isScheduledClientVisit = (visit, now = Date.now()) => {
  const scheduledAt = scheduledVisitDateValue(visit);
  const time = new Date(scheduledAt || 0).getTime();

  return (
    SCHEDULED_CLIENT_VISIT_STATUSES.includes(visit?.status) &&
    Number.isFinite(time) &&
    time >= Number(now)
  );
};

export const formatScheduledClientVisitLabel = value => {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '—';

  const day = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

  return `${day} às ${time}`;
};

export const formatScheduledClientSince = value => {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const compareByTime = (a, b) =>
  new Date(a.nextVisitScheduledTo || 0).getTime() -
  new Date(b.nextVisitScheduledTo || 0).getTime();

const compareByCreatedAtDesc = (a, b) =>
  new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

const compareByName = (a, b) => a.name.localeCompare(b.name, 'pt-BR');

export const buildScheduledClientCards = ({
  visits = [],
  orderBy = SCHEDULED_VISITS_ORDER_BY_QUERY.CLOSER_VISITS,
  now = Date.now(),
  getContactPhone = () => '',
} = {}) => {
  const byContact = new Map();

  visits.forEach(visit => {
    if (!isScheduledClientVisit(visit, now)) return;

    const id = scheduledClientId(visit);
    if (!id || byContact.has(id)) return;

    const nextVisitScheduledTo = scheduledVisitDateValue(visit);
    const createdAt = scheduledClientCreatedAt(visit);

    byContact.set(id, {
      id,
      userClientId: id,
      name: scheduledClientName(visit),
      clientFullName: scheduledClientName(visit),
      phone: getContactPhone(visit),
      clientPhone: getContactPhone(visit),
      createdAt,
      clientSince: formatScheduledClientSince(createdAt),
      nextVisitScheduledTo,
      nextVisitLabel: `Próxima visita: ${formatScheduledClientVisitLabel(
        nextVisitScheduledTo
      )}`,
      visit,
    });
  });

  const clients = [...byContact.values()];

  if (orderBy === SCHEDULED_VISITS_ORDER_BY_QUERY.NAME) {
    return clients.sort(compareByName);
  }

  if (orderBy === SCHEDULED_VISITS_ORDER_BY_QUERY.NEW_CLIENTS) {
    return clients.sort(compareByCreatedAtDesc);
  }

  return clients.sort(compareByTime);
};
